import React from 'react';
import { RECAPTCHA_SITE_KEY } from '@env';
import { Platform, StyleSheet, View } from 'react-native';
import ButtonV2 from '../../components/shared/Buttons/ButtonV2.container';
import I18n from '../../components/shared/i18n/I18n.component';
import ImageIcon from '../../components/shared/Icon/ImageIcon.component';
import colors from '../../config/colors';
import SignInV2Layout from '../SignInV2/components/SignInV2Layout';
import ReCAPTCHA from 'react-google-recaptcha';
import { OtpTarget } from '../SignInV2/SignInV2.utils';

interface ForgotPasswordProps {
	didResetPassword: boolean;
	onBack: () => void;
	sendEmailForgotPsw: () => void;
	hasError: boolean;
	onCaptchaChange: (value: any) => void;
	onExpiredCaptcha: () => void;
	captchaVerified: boolean;
	submitted: boolean;
	/** where the reset link was sent - mail unless the employee has none */
	resetTarget: OtpTarget;
}

const ForgotPassword = React.forwardRef(
	(
		{
			didResetPassword,
			onBack,
			sendEmailForgotPsw,
			hasError,
			onCaptchaChange,
			onExpiredCaptcha,
			captchaVerified,
			submitted,
			resetTarget,
		}: ForgotPasswordProps,
		ref: any,
	) => {
		const isPhone = resetTarget.type === 'phone';
		// the masked destination is a nice-to-have: without it the text still has
		// to name the channel, so each one keeps a target-less variant
		const subtitleKey = resetTarget.masked
			? isPhone
				? 'signInV2.forgot.sent.subtitlePhone'
				: 'signInV2.forgot.sent.subtitleMail'
			: isPhone
			? 'signInV2.forgot.sent.subtitlePhoneNoTarget'
			: 'signInV2.forgot.sent.subtitleMailNoTarget';

		return (
			<SignInV2Layout>
				{didResetPassword ? (
					<>
						<View style={styles.sentIllustration}>
							<ImageIcon name='bluePaper' width={100} height={100} />
						</View>
						<I18n
							size={17}
							font='medium'
							color='primaryDarkBlue'
							style={styles.sentTitle}
						>
							signInV2.forgot.sent.title
						</I18n>
						<I18n
							size={14}
							color='subDarkBlue'
							style={styles.sentSubtitle}
							target={resetTarget.masked}
						>
							{subtitleKey}
						</I18n>
						<I18n
							size={14}
							font='medium'
							color='primaryDarkBlue'
							style={styles.sentHint}
						>
							{isPhone
								? 'signInV2.forgot.sent.hintPhone'
								: 'signInV2.forgot.sent.hintMail'}
						</I18n>
						<ButtonV2
							preset='primary'
							size='medium'
							onPress={onBack}
							overrideStyle={{ width: '100%' }}
						>
							<I18n size={15} weight='normal' color='white'>
								signInV2.forgot.sent.back
							</I18n>
						</ButtonV2>
					</>
				) : (
					<>
						<I18n
							size={17}
							font='medium'
							color='primaryDarkBlue'
							style={styles.title}
						>
							signInV2.forgot.title
						</I18n>
						<I18n
							size={14}
							color='subDarkBlue'
							style={styles.subtitle}
						>
							signInV2.forgot.subtitle
						</I18n>
						{Platform.OS === 'web' ? (
							<View style={styles.captchaRow}>
								<ReCAPTCHA
									ref={ref}
									onChange={onCaptchaChange}
									onExpired={onExpiredCaptcha}
									sitekey={RECAPTCHA_SITE_KEY}
								/>
							</View>
						) : null}
						{Platform.OS === 'web' && !captchaVerified && submitted && (
							<View style={styles.errorRow}>
								<ImageIcon
									name={'alertCircle'}
									color={colors.failure}
									width={15}
									height={15}
									style={{ marginLeft: 4 }}
								/>
								<I18n color='failure' weight='normal' size={12}>
									forgetPassword.invalidCaptcha
								</I18n>
							</View>
						)}
						{hasError && (
							<View style={styles.errorRow}>
								<ImageIcon
									name={'alertCircle'}
									color={colors.failure}
									width={15}
									height={15}
									style={{ marginLeft: 4 }}
								/>
								<I18n color='failure' weight='normal' size={12}>
									forgetPassword.invalidTZ
								</I18n>
							</View>
						)}
						<View style={styles.submitRow}>
							<ButtonV2
								preset='primary'
								size='medium'
								disabled={
									Platform.OS === 'web' && !captchaVerified
								}
								onPress={() => sendEmailForgotPsw()}
								overrideStyle={{ width: '100%' }}
							>
								<I18n size={15} weight='normal' color='white'>
									signInV2.forgot.submit
								</I18n>
							</ButtonV2>
						</View>
						<View style={styles.backRow}>
							<ButtonV2
								preset='link'
								size='medium'
								onPress={onBack}
								overrideStyle={{ width: '100%' }}
							>
								<I18n size={14} weight='400' color='titlePrimary'>
									signInV2.back
								</I18n>
							</ButtonV2>
						</View>
					</>
				)}
			</SignInV2Layout>
		);
	},
);

const styles = StyleSheet.create({
	title: {
		textAlign: 'center',
		marginBottom: 16,
		letterSpacing: 0.15,
	},
	subtitle: {
		textAlign: 'center',
		marginBottom: 32,
	},
	captchaRow: {
		alignItems: 'center',
		marginBottom: 16,
	},
	errorRow: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
	},
	submitRow: {
		marginTop: 16,
	},
	backRow: {
		marginTop: 12,
	},
	sentIllustration: {
		alignItems: 'center',
		marginTop: 24,
		marginBottom: 40,
	},
	sentTitle: {
		textAlign: 'center',
		marginBottom: 12,
		letterSpacing: 0.15,
	},
	sentSubtitle: {
		textAlign: 'center',
		lineHeight: 20,
		marginBottom: 24,
	},
	sentHint: {
		textAlign: 'center',
		marginBottom: 32,
	},
});

export default ForgotPassword;
